// ============================================================
// ALBATROS — ScannedItem.tsx
// Card de item escaneado en el carrito
// ============================================================

import React from 'react';
import {
  BookOpen,
  Minus,
  Plus,
  Trash2,
  Ticket,
  Sparkles,
  Clock
} from 'lucide-react';
import { formatCurrency } from '../../utils/currency';
import { CartItem } from '../../types/book.types';

interface ScannedItemProps {
  item: CartItem;
  onQtyChange: (delta: number) => void;
  onDiscount: () => void;
  onRecommendations: () => void;
  isReturnMode?: boolean;
}

export default function ScannedItem({
  item,
  onQtyChange,
  onDiscount,
  onRecommendations,
  isReturnMode = false
}: ScannedItemProps) {
  const lineTotal = item.price * item.quantity * (1 - item.discount / 100);

  // El stock viene de books_list. En devoluciones no limita: ahi las unidades
  // vuelven al almacen, no salen de el. MainApp lo comprueba otra vez al pulsar
  // (el escaneo entra por addToCart sin pasar por aqui); esto solo evita que el
  // cajero llegue a pulsar un boton que no va a hacer nada.
  // Una linea de PEDIDO (con proveedor) se pide, no sale del almacen: sin tope.
  const atStockLimit = !isReturnMode && !item.selectedSource && item.quantity >= item.stock;

  return (
    <div className="al-item-card">

      {/* Thumbnail */}
      <div className="al-item-thumb">
        <BookOpen style={{ width: '1.5rem', height: '1.5rem' }} />
      </div>

      {/* Info */}
      <div className="al-item-info">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.125rem' }}>
          <h4 className="al-item-title">{item.title}</h4>
          {item.selectedSource && (
            <span className="al-item-order-badge">PEDIDO</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span className="al-item-author">{item.author}</span>
          <span style={{ width: '0.25rem', height: '0.25rem', background: '#D4D4D4', borderRadius: '50%' }} />
          <span className="al-item-isbn">{item.isbn}</span>
        </div>
        {item.selectedSource && (
          <div className="al-item-source">
            <Clock style={{ width: '0.75rem', height: '0.75rem' }} />
            {item.selectedSource.name}{item.selectedSource.waitTime ? ` • ${item.selectedSource.waitTime}` : ''}
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>

        {/* Qty */}
        <div className="al-qty-wrap">
          <button
            onClick={() => onQtyChange(-1)}
            className="al-qty-btn al-qty-btn--minus"
          >
            <Minus style={{ width: '1rem', height: '1rem' }} />
          </button>
          <span className="al-qty-value">{item.quantity}</span>
          <button
            onClick={() => onQtyChange(1)}
            disabled={atStockLimit}
            title={atStockLimit ? `Sin mas stock: quedan ${item.stock}` : undefined}
            aria-label="Aumentar cantidad"
            className={`al-qty-btn al-qty-btn--plus${atStockLimit ? ' al-qty-btn--at-max' : ''}`}
          >
            <Plus style={{ width: '1rem', height: '1rem' }} />
          </button>
        </div>

        {/* Price */}
        <div className="al-item-price">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem' }}>
            {item.discount > 0 && (
              <span className="al-item-discount-badge">-{item.discount}%</span>
            )}
            <span className="al-item-total">{formatCurrency(lineTotal)}</span>
          </div>
          <p className="al-item-unit">{formatCurrency(item.price)}/U</p>
        </div>

        {/* Actions */}
        <div className="al-item-actions">
          {!isReturnMode && (
            <>
              <button
                onClick={onDiscount}
                className="al-item-action-btn al-item-action-btn--discount"
              >
                <Ticket style={{ width: '1.25rem', height: '1.25rem' }} />
              </button>
              <button
                onClick={onRecommendations}
                className="al-item-action-btn al-item-action-btn--sparkle"
              >
                <Sparkles style={{ width: '1.25rem', height: '1.25rem' }} />
              </button>
            </>
          )}
          <button
            onClick={() => onQtyChange(-999)}
            className="al-item-action-btn al-item-action-btn--delete"
          >
            <Trash2 style={{ width: '1.25rem', height: '1.25rem' }} />
          </button>
        </div>

      </div>
    </div>
  );
}